import { Platform } from 'react-native';

/**
 * Where the app talks to.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A FILE AND NOT A CONSTANT IN THE CLIENT
 * ---------------------------------------------------------------------------
 * There are three places the API can live and they are not interchangeable -
 * "localhost" means the phone, not your laptop, and that one fact is behind
 * almost every "the backend works in Swagger but the app times out" morning:
 *
 *   Deployed          a real host over HTTPS, reachable from anywhere
 *   Android emulator  10.0.2.2 is the emulator's alias for the host machine
 *   iOS simulator     shares the host's network stack, so localhost is right
 *   Physical device    the host's LAN address - the phone has to route to it
 *
 * So all of them are written down here, and `ORIGIN` picks one. Everything
 * else in the app reads `API_BASE_URL`.
 */

/**
 * The deployed backend.
 *
 * HTTPS and no port - unlike the local origins below, which are plain HTTP on
 * 3000. That difference is why this file builds whole origins rather than
 * assembling `http://${host}:${port}`, which could not express this one.
 *
 * Moved off `homingo-backend.onrender.com` on 24 Aug 2026. The old host is
 * kept below rather than deleted: it is still a running deployment, and the
 * two are NOT interchangeable - see the note on `LEGACY_RENDER_ORIGIN`.
 */
const REMOTE_ORIGIN = 'https://api.itshomingo.com';

/**
 * The previous deployment, on Render's free tier.
 *
 * Not dead code and not a fallback - nothing selects it automatically, because
 * silently failing over between two backends that may hold different data is
 * worse than a clear error. It is written down because it is where every build
 * before this change pointed, and because it is the thing to compare against
 * when the new host behaves differently from what the app expects.
 *
 * It also explains `API_TIMEOUT_MS` below: the long timeout exists for
 * Render's cold starts, and this is the host that had them.
 */
const LEGACY_RENDER_ORIGIN = 'https://homingo-backend.onrender.com';

/**
 * This machine's address on the Wi-Fi, as of when this was written.
 *
 * A DHCP lease is not forever. When the app starts timing out on a device that
 * worked yesterday, this is the first thing to check:
 *
 *   Windows   ipconfig            -> IPv4 Address under your Wi-Fi adapter
 *   macOS     ipconfig getifaddr en0
 *
 * The phone and the laptop must be on the same network, and Windows Firewall
 * must allow inbound TCP on the port - a fresh install blocks it, and the
 * symptom is a timeout rather than a refusal, which reads like a slow server.
 */
const LAN_ORIGIN = 'http://192.168.29.105:3000';

const EMULATOR_ORIGIN =
  Platform.OS === 'android' ? 'http://10.0.2.2:3000' : 'http://localhost:3000';

/**
 * A real handset on the USB cable, via `adb reverse tcp:3000 tcp:3000`.
 *
 * Preferred over `LAN_ORIGIN` for a wired device, and the reason is everything
 * the note above `LAN_ORIGIN` lists: no DHCP lease to go stale, no firewall
 * rule to add, and no router that refuses to route between two of its own
 * clients. The handset's own port 3000 simply becomes the laptop's.
 *
 * The one thing it needs is that command. Without it this address reaches
 * nothing and every request hangs to `API_TIMEOUT_MS`, which reads like a slow
 * server rather than a missing bridge.
 */
const REVERSE_ORIGIN = 'http://localhost:3000';

/**
 * Which backend this build talks to. Change this one line.
 *
 * On `REMOTE_ORIGIN` the app needs no backend running locally and works off
 * your network entirely - but it is writing to the real database and its OTPs
 * are real SMS. Switch to `REVERSE_ORIGIN` (with `adb reverse tcp:3000
 * tcp:3000`) for a cabled handset, or `LAN_ORIGIN` over Wi-Fi.
 */
/* Annotated `string` rather than left to inference. Without it TypeScript
   narrows this to the one literal it was assigned, and `ORIGIN === REMOTE_ORIGIN`
   below stops compiling - a switch that breaks the build when you use it is not
   the one-line switch this file advertises. */
/* SHIPPING. This line decides where every handset sends every request, and it
   is the one thing an over-the-air update cannot be wrong about: a release
   carrying `REVERSE_ORIGIN` points a hundred thousand phones at a laptop that
   is not theirs, and nothing in the app loads at all.

   Put it back to REMOTE_ORIGIN before committing, every time. */
const ORIGIN: string = REMOTE_ORIGIN;

/** Must match API_PREFIX/API_VERSION in the backend's environment. */
const PREFIX = 'api/v1';

export const API_BASE_URL = `${ORIGIN}/${PREFIX}`;

/**
 * The same origin, without the API prefix.
 *
 * The live gateway is NOT under `api/v1`. Socket.IO mounts at the server
 * root - `/socket.io` for the handshake, with the namespace sent as part of
 * the protocol rather than as a path - so a client handed `API_BASE_URL`
 * would knock on `/api/v1/socket.io`, get a 404, and retry forever without
 * the log ever saying anything more useful than "connection error".
 */
export const API_ORIGIN = ORIGIN;

/**
 * How long a request may hang before the app gives up on it.
 *
 * `fetch` has no timeout and never gives up on its own: a phone that has
 * wandered off Wi-Fi leaves the request unsettled forever, and the default is
 * a spinner that spins until the app is killed. Better to fail, say so, and
 * offer Retry.
 *
 * ---------------------------------------------------------------------------
 * WHY THE DEPLOYED ONE GETS SO MUCH LONGER
 * ---------------------------------------------------------------------------
 * Ten seconds is right for a server on the same desk. It was wrong for the old
 * free Render instance, which SPUN DOWN when idle and took 30-60 seconds to
 * wake: at ten seconds the first request after a quiet spell always aborted,
 * and the app said "That took too long. Check your connection and try again."
 * - which reads as a bug in the app and was actually a cold start.
 *
 * KEPT AT SIXTY FOR NOW, THOUGH THE HOST HAS CHANGED. `api.itshomingo.com`
 * answered its health check promptly when this was switched over, so the cold
 * start may well be gone - but a timeout that is too long only costs a slow
 * failure on a request that was going to fail anyway, while one that is too
 * short breaks working requests and blames the customer's connection for it.
 * Drop it to ten once the new host has been watched over a quiet period and
 * shown not to sleep.
 */
export const API_TIMEOUT_MS = ORIGIN === REMOTE_ORIGIN ? 60_000 : 10_000;

/* Referenced so the origins that are not selected do not become dead code the
   moment someone switches `ORIGIN` - and so changing between them stays a
   one-word edit. */
export const ORIGINS = {
  remote: REMOTE_ORIGIN,
  legacyRender: LEGACY_RENDER_ORIGIN,
  lan: LAN_ORIGIN,
  emulator: EMULATOR_ORIGIN,
  reverse: REVERSE_ORIGIN,
} as const;
